import { PieChart } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled } from "@mui/material/styles";
import { Box, Stack, Typography } from "@mui/material";
import PublishedWithChangesRoundedIcon from "@mui/icons-material/PublishedWithChangesRounded";
import { computeDonutBuckets } from "../utils/helpersOfBrands";

const size = { width: 120, height: 120 };

const CenterNumber = styled("text")(() => ({
  fill: "#000",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontWeight: 700,
}));

function PieCenterLabel({ total }: { total: number }) {
  const { width, height, left, top } = useDrawingArea();
  const centerX = left + width / 2;
  const centerY = top + height / 2;
  const digits = String(total).length;
  const fontSize =
    digits <= 2 ? 23 : digits === 3 ? 18 : digits === 4 ? 15 : 12;

  return (
    <CenterNumber x={centerX} y={centerY} style={{ fontSize }}>
      {total}
    </CenterNumber>
  );
}

function CustomLegend({ data }: { data: ReturnType<typeof computeDonutBuckets> }) {
  return (
    <Box display="grid" gridTemplateColumns="repeat(4, max-content)" gap="10px 24px">
      {data.map((entry) => (
        <Box key={entry.key} display="flex" alignItems="center" gap="6px">
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "3px",
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: "13px", color: "#00000099" }}>
            {entry.label}
          </Typography>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#000" }}>
            {entry.count} ({entry.percent})
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

interface Props {
  rows: any[];
}

// Same visual/display logic as BrandConfigurationReportChart.tsx (legacy
// module, untouched), but sourced from this tab's own rows and bucketed by
// the query's precomputed donut_bucket field instead of re-deriving buckets
// from integration_status/step — the new query already computes them.
const NeedsConfigurationChart = ({ rows }: Props) => {
  const chartData = computeDonutBuckets(rows);
  const total = chartData.reduce((sum, d) => sum + d.count, 0);

  if (chartData.length === 0 || total === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "#F7F7F8",
        borderRadius: "10px",
        px: 2.5,
        py: 2,
        mb: 3,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <PublishedWithChangesRoundedIcon sx={{ fontSize: 20, color: "#8E24AA" }} />
        <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#000000DE" }}>
          Quick View: Integration Status
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ flexShrink: 0, width: size.width, height: size.height }}>
          <PieChart
            margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            series={[
              {
                data: chartData.map((d) => ({
                  label: d.label,
                  value: d.count,
                  color: d.color,
                })),
                innerRadius: 40,
                outerRadius: 50,
              } as any,
            ]}
            {...size}
            slotProps={{ legend: { hidden: true } }}
          >
            <PieCenterLabel total={total} />
          </PieChart>
        </Box>
        <CustomLegend data={chartData} />
      </Stack>
    </Box>
  );
};

export default NeedsConfigurationChart;
